import { formatDate } from '../../lib/format';
import type { ChangeRequest } from '../../lib/types';

/**
 * The approval questionnaire — the one page that goes to whoever decides.
 *
 * It is the request AND the questions in one document, because sending a
 * proposal and asking for "thoughts" is how a decision takes three weeks. The
 * questions are closed and few: each one is answerable in a word, and together
 * they are the decision.
 *
 * WHAT IS MISSING IS PRINTED AS MISSING. A blank rollback plan appears as
 * "Not answered" in crimson rather than as an empty space, because the gap is
 * the most useful thing on the page — an approver should see that nobody has
 * said how this gets undone, and be able to send it back for that reason.
 * Hiding empty fields would produce a tidier document and a worse decision.
 *
 * Rendered into a detached iframe and printed, the same harness as the
 * scorecard and the case brief. Colours are literal hex because the iframe is
 * a separate document and never sees tokens.css.
 */

const NAVY = '#14395C'; /* --brand-900 */
const INK = '#2F4050'; /* --gray-700 */
const MUTED = '#546778'; /* --gray-500 */
const RULE = '#DCE3EA'; /* --gray-200 */
const WASH = '#F4F7FA'; /* --brand-50 */
const CRIMSON = '#AE1920'; /* --accent-600 */

const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

const nl2br = (s: unknown): string => esc(s).replace(/\r?\n/g, '<br>');

/** A field, or a visible gap where one should be. */
const answer = (v: string | null): string =>
  v && v.trim() ? nl2br(v) : `<span class="gap">Not answered</span>`;

const money = (n: number | null): string =>
  n === null || n === undefined
    ? `<span class="gap">Not answered</span>`
    : `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} a year`;

const KIND_LABEL: Record<string, string> = {
  process: 'Change to a process',
  tool: 'New tool request',
};

function styles(): string {
  return `
    @page { size: letter; margin: 16mm 15mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0; color: ${INK}; background: #fff;
      font: 10.5pt/1.45 "Nunito Sans", "Segoe UI", Helvetica, Arial, sans-serif;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    h1, h2 { font-family: "Baloo 2", "Nunito Sans", Helvetica, Arial, sans-serif; }
    h1 { font-size: 19pt; font-weight: 800; color: ${NAVY}; margin: 0 0 1mm; letter-spacing: -.2pt; }
    h2 {
      font-size: 10.5pt; font-weight: 700; color: ${NAVY}; text-transform: uppercase;
      letter-spacing: .08em; margin: 7mm 0 2mm; padding-bottom: 1mm;
      border-bottom: 1px solid ${RULE}; break-after: avoid;
    }
    .sub { color: ${MUTED}; margin: 0 0 4mm; font-size: 10pt; }
    .gap { color: ${CRIMSON}; font-style: italic; }

    table.meta { width: 100%; border-collapse: collapse; margin: 0 0 2mm; }
    table.meta td { padding: 0 4mm 2mm 0; vertical-align: top; }
    .k { font-size: 8pt; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: ${MUTED}; }
    .v { font-size: 10.5pt; color: ${NAVY}; font-weight: 600; }

    .field { margin: 0 0 3mm; break-inside: avoid; }
    .field .k { margin-bottom: 1mm; }

    /* The questions. Boxed because this half of the page is the bit the
       approver writes on, and it should look like it. */
    .q {
      border: 1px solid ${RULE}; border-radius: 2mm; padding: 3mm 4mm; margin: 0 0 2.5mm;
      break-inside: avoid;
    }
    .q-text { font-weight: 600; color: ${NAVY}; margin: 0 0 2mm; }
    .opts { font-size: 10pt; color: ${INK}; }
    .opts span { margin-right: 6mm; white-space: nowrap; }
    .box {
      display: inline-block; width: 3.6mm; height: 3.6mm; border: 1px solid ${MUTED};
      margin-right: 1.5mm; vertical-align: -0.4mm;
    }
    .writein { border-bottom: 1px solid ${RULE}; height: 7mm; margin-top: 2mm; }

    .sign { margin-top: 8mm; display: flex; gap: 10mm; }
    .sign div { flex: 1; }
    .sign .line { border-bottom: 1px solid ${MUTED}; height: 9mm; }
    .sign .k { margin-top: 1.5mm; }

    .foot { margin-top: 8mm; padding-top: 2mm; border-top: 1px solid ${RULE}; font-size: 8.5pt; color: ${MUTED}; }
    .wash { background: ${WASH}; padding: 3mm 4mm; border-radius: 2mm; }
  `;
}

/** One closed question with tick boxes, and room to qualify the answer. */
const question = (text: string, options: string[], writein = false): string => `
  <div class="q">
    <p class="q-text">${esc(text)}</p>
    <div class="opts">${options
      .map((o) => `<span><span class="box"></span>${esc(o)}</span>`)
      .join('')}</div>
    ${writein ? '<div class="writein"></div>' : ''}
  </div>`;

export function buildApprovalHtml(c: ChangeRequest): string {
  const isTool = c.Kind === 'tool';

  const field = (label: string, value: string | null): string =>
    `<div class="field"><div class="k">${esc(label)}</div><div>${answer(value)}</div></div>`;

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>Approval — ${esc(c.Title)}</title><style>${styles()}</style></head><body>
  <h1>${esc(c.Title)}</h1>
  <p class="sub">${esc(KIND_LABEL[c.Kind] ?? c.Kind)} &middot; for approval</p>

  <table class="meta"><tr>
    <td><div class="k">Raised by</div><div class="v">${esc(c.RequestedByName || '—')}</div></td>
    <td><div class="k">Priority</div><div class="v">${esc(c.Priority)}</div></td>
    <td><div class="k">Impact</div><div class="v">${esc(c.ImpactLevel)}</div></td>
    <td><div class="k">Wanted by</div><div class="v">${
      c.TargetDate ? esc(formatDate(c.TargetDate)) : 'No date'
    }</div></td>
  </tr></table>

  <h2>The problem</h2>
  <div class="wash">${answer(c.Problem)}</div>

  <h2>What is proposed</h2>
  <div>${answer(c.Proposal)}</div>

  ${
    isTool
      ? `<h2>The tool</h2>
  <table class="meta"><tr>
    <td><div class="k">Tool</div><div class="v">${esc(c.ToolName || '—')}</div></td>
    <td><div class="k">Vendor</div><div class="v">${esc(c.Vendor || '—')}</div></td>
    <td><div class="k">Cost</div><div class="v">${money(c.AnnualCost)}</div></td>
  </tr></table>
  ${field('What it replaces', c.Replaces)}
  ${field('Data it would hold', c.DataHandled)}`
      : ''
  }

  <h2>Why it is worth doing</h2>
  <div>${answer(c.Justification)}</div>

  <h2>Who it affects</h2>
  <div>${answer(c.WhoIsAffected)}</div>

  <h2>Risk, and what we would do about it</h2>
  <div>${answer(c.RiskAndMitigation)}</div>

  <h2>How it gets undone</h2>
  <div>${answer(c.RollbackPlan)}</div>

  <h2>How we will know it worked</h2>
  <div>${answer(c.SuccessMeasure)}</div>

  <h2>Your decision</h2>
  ${question('Is this a problem worth solving now?', ['Yes', 'Not yet', 'No'])}
  ${question(
    isTool ? 'Is the cost proportionate to the problem?' : 'Is the disruption proportionate to the problem?',
    ['Yes', 'No'],
  )}
  ${question('Are you satisfied with the risk and the way back out?', ['Yes', 'No — see conditions'])}
  ${question(
    'Decision',
    ['Approve', 'Approve with conditions', 'Defer', 'Decline'],
    true,
  )}
  ${question('Conditions, or what would change your mind', [], true)}

  <div class="sign">
    <div><div class="line"></div><div class="k">Signed</div></div>
    <div><div class="line"></div><div class="k">Date</div></div>
  </div>

  <p class="foot">
    Anything marked <span class="gap">Not answered</span> has not been filled in. That is shown
    rather than hidden — an unanswered rollback plan is a reason to send this back, not a
    formatting problem.
  </p>
</body></html>`;
}

/**
 * Render and hand to the browser's print dialog. Same harness as the scorecard
 * and case brief exports: no dependency, prints what the reader sees, and
 * "Save as PDF" is a destination in every modern print dialog.
 */
export function exportApprovalPdf(c: ChangeRequest): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);

  const done = () => {
    if (frame.parentNode) frame.remove();
  };

  frame.onload = () => {
    const win = frame.contentWindow;
    if (!win) {
      done();
      return;
    }
    try {
      win.focus();
      win.onafterprint = done;
      win.print();
      setTimeout(done, 60_000);
    } catch {
      done();
    }
  };

  frame.srcdoc = buildApprovalHtml(c);
}
